import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, RefreshCw, Server, AlertCircle, ShoppingBag } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { ModuleLayout } from '../../ui/ModuleLayout';

interface Product {
    id: number;
    name: string;
    price: number;
}

export const ReactEffects = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // Module State
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [triggerCount, setTriggerCount] = useState(0);

    // Mock API
    const fetchProducts = async (shouldFail = false) => {
        setLoading(true);
        setError(null);
        setProducts([]);

        // Artificial Delay
        await new Promise(resolve => setTimeout(resolve, 2000));

        if (shouldFail) {
            setError("500: Server Fire. Please try again.");
            setLoading(false);
            return;
        }

        setProducts([
            { id: 1, name: 'Quantum Banana', price: 10 },
            { id: 2, name: 'Cyber Apple', price: 5 },
            { id: 3, name: 'Glitch Grapes', price: 8 },
        ]);
        setLoading(false);
        setTriggerCount(c => c + 1);
    };

    // Win Condition
    useEffect(() => {
        if (!completed && products.length > 0 && triggerCount >= 1) {
            // Need one successful fetch
            setCompleted(true);
            completeModule('react-effects', 100);
            setShowSuccess(true);
        }
    }, [products, triggerCount]);

    return (
        <ModuleLayout
            moduleId="react-effects"
            title="The Spark: useEffect"
            description="Talking to the outside world."
        >
            <div className="bg-slate-900 min-h-full font-sans text-slate-100 p-8">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-10">
                        <h1 className="text-3xl font-black font-comic text-yellow-400 mb-2 flex items-center justify-center gap-3">
                            <Sparkles size={32} /> Side Effects
                        </h1>
                        <p className="text-slate-400">
                            React components are pure... until they need to talk to a server.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Browser View */}
                        <div className="bg-slate-800 p-6 rounded-3xl border border-slate-700 min-h-[300px] flex flex-col relative overflow-hidden">
                            <div className="absolute top-4 left-4 text-xs font-bold text-slate-500 uppercase tracking-widest">
                                Browser Render
                            </div>

                            <div className="mt-10 flex-1 flex flex-col items-center justify-center">
                                {loading && (
                                    <div className="flex flex-col items-center gap-2 text-yellow-400">
                                        <RefreshCw className="animate-spin" size={32} />
                                        <span className="font-bold">Fetching Data...</span>
                                    </div>
                                )}

                                {error && (
                                    <div className="flex flex-col items-center gap-2 text-red-400">
                                        <AlertCircle size={32} />
                                        <span className="font-bold">{error}</span>
                                    </div>
                                )}

                                {!loading && !error && products.length === 0 && (
                                    <div className="text-slate-500 italic">No Data Loaded</div>
                                )}

                                <div className="grid grid-cols-1 gap-3 w-full max-w-xs">
                                    <AnimatePresence>
                                        {products.map(p => (
                                            <motion.div
                                                key={p.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="bg-slate-700 p-3 rounded-lg flex justify-between items-center border border-slate-600"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <ShoppingBag size={16} className="text-blue-400" />
                                                    <span className="font-bold">{p.name}</span>
                                                </div>
                                                <span className="bg-slate-900 px-2 py-1 rounded text-xs text-green-400 font-mono">
                                                    ${p.price}
                                                </span>
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>

                        {/* Controls */}
                        <div className="space-y-6">
                            <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800 font-mono text-xs relative overflow-hidden">
                                <div className="text-purple-400">useEffect(() ={'>'} {'{'}</div>
                                <div className="pl-4 text-slate-400">// This runs when component mounts</div>
                                <div className="pl-4 text-green-400">fetch('/api/products')</div>
                                <div className="pl-8 text-blue-300">.then(data ={'>'} setProducts(data))</div>
                                <div className="pl-8 text-red-400">.catch(err ={'>'} setError(err));</div>
                                <div className="text-purple-400">{'}'}, []);</div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() => fetchProducts(false)}
                                    disabled={loading}
                                    className="p-4 rounded-xl bg-green-600 hover:bg-green-500 text-white font-bold flex flex-col items-center gap-2 disabled:opacity-50"
                                >
                                    <Server size={24} />
                                    Connect to API
                                </button>

                                <button
                                    onClick={() => fetchProducts(true)}
                                    disabled={loading}
                                    className="p-4 rounded-xl bg-red-900/50 hover:bg-red-900/80 text-red-200 border border-red-800 font-bold flex flex-col items-center gap-2 disabled:opacity-50"
                                >
                                    <AlertCircle size={24} />
                                    Simulate Error
                                </button>
                            </div>

                            <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-xl text-yellow-200 text-sm">
                                <strong>Tip:</strong> Users hate waiting. Always show a generic spinner while `loading` is true.
                            </div>
                        </div>
                    </div>
                </div>

                {showSuccess && (
                    <ModuleSuccess
                        title="Connection Established!"
                        onRetry={() => setShowSuccess(false)}
                    >
                        <div className="space-y-4">
                            <p className="text-slate-600">
                                You retrieved data from the "server".
                            </p>
                            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                <h4 className="font-bold text-blue-800 text-sm mb-1">The Lifecycle</h4>
                                <p className="text-blue-700 text-xs">
                                    Components are born (Mount), they change (Update), and they die (Unmount). `useEffect` lets us run code at these specific moments.
                                </p>
                            </div>
                        </div>
                    </ModuleSuccess>
                )}

                {/* Educational Content Section */}
                <div className="max-w-4xl mx-auto mt-12 space-y-8 pb-12">

                    {/* Theory Section */}
                    <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700">
                        <h2 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
                            📚 Understanding Effects
                        </h2>

                        <div className="grid md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="font-bold text-lg text-yellow-400 mb-3">Syncing with the World</h3>
                                <p className="text-slate-300 mb-4">
                                    React is just functions returning UI. But real apps need to do messy things: fetch data, listen to key presses, or start timers.
                                </p>
                                <p className="text-slate-300 mb-4">
                                    <strong>Side Effects</strong> are actions that reach <em>outside</em> the component.
                                </p>

                                <div className="bg-purple-900/30 p-4 rounded-xl border border-purple-500/30 mt-4">
                                    <h4 className="font-bold text-purple-300 mb-2">The Dependency Array `[]`</h4>
                                    <p className="text-slate-400 text-xs mb-3">This tells React <strong>when</strong> to run your effect.</p>
                                    <ul className="text-slate-300 text-xs space-y-2 font-mono ml-2">
                                        <li>
                                            <span className="text-green-400">[]</span>
                                            <span className="text-slate-500 ml-2">// Run once (Mount)</span>
                                        </li>
                                        <li>
                                            <span className="text-blue-400">[id]</span>
                                            <span className="text-slate-500 ml-2">// Run when `id` changes</span>
                                        </li>
                                        <li>
                                            <span className="text-red-400">Missing</span>
                                            <span className="text-slate-500 ml-2">// Run every render (Danger!)</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-600">
                                <h3 className="font-bold text-lg text-white mb-3">🧹 The Cleanup Function</h3>
                                <p className="text-slate-400 text-xs mb-4">
                                    Sometimes you make a mess (start a timer, subscribe to chat). You must clean it up when the component dies!
                                </p>

                                <div className="font-mono text-xs bg-black/50 p-4 rounded-lg text-slate-300 border-l-4 border-green-500">
                                    <div>useEffect(() ={'>'} {'{'}</div>
                                    <div className="text-slate-500 pl-4">// 1. Setup</div>
                                    <div className="pl-4">const timer = setInterval(...)</div>
                                    <div className="pl-4 mt-2 mb-2 text-green-400">return () ={'>'} {'{'}</div>
                                    <div className="text-slate-500 pl-8">// 2. Cleanup</div>
                                    <div className="pl-8">clearInterval(timer);</div>
                                    <div className="pl-4 text-green-400">{'}'};</div>
                                    <div>{'}'}, []);</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Common Pitfalls */}
                    <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 p-8 rounded-3xl border border-red-500/20">
                        <h2 className="text-xl font-bold text-red-200 mb-4">⚠️ Infinite Loops</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700">
                                <h3 className="font-bold text-red-400 mb-2">The Problem</h3>
                                <p className="text-slate-400 text-sm">
                                    If your effect updates state (`setCount`), AND depends on that state (`[count]`), it will run forever!
                                </p>
                            </div>
                            <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700">
                                <h3 className="font-bold text-green-400 mb-2">The Fix</h3>
                                <p className="text-slate-400 text-sm">
                                    Trust the dependency linter. If you need to update based on previous state, use the functional update: `setCount(c ={'>'} c + 1)`.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ModuleLayout>
    );
};
